import React, { useMemo } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Task, TaskPriority } from 'public-contracts';
import { useTranslation } from '../../hooks/useTranslation';
import { Calendar, User as UserIcon, Clock } from 'lucide-react';
import { format } from 'date-fns';
import { User } from '../../services/usersService';
import sdk from '../../sdks/master-data-api';
import { authenticatedFetch } from '../../utils/authenticatedFetch';

interface ProjectTaskCardProps {
  task: Task;
  users: User[];
  onEdit?: (task: Task) => void;
  onDragStart?: (e: React.DragEvent, taskId: string) => void;
}

export const ProjectTaskCard: React.FC<ProjectTaskCardProps> = ({
  task,
  users,
  onEdit,
  onDragStart,
}) => {
  const { translate } = useTranslation();

  const assignee = useMemo(() => {
    return users.find((u) => u.id === task.assigneeId);
  }, [users, task.assigneeId]);

  const getPriorityColor = (priority: TaskPriority) => {
    switch (priority) {
      case TaskPriority.URGENT:
        return 'bg-red-100 text-red-800 border-red-200';
      case TaskPriority.HIGH:
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case TaskPriority.MEDIUM:
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case TaskPriority.LOW:
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const handleDragStart = (e: React.DragEvent) => {
    if (onDragStart) {
      onDragStart(e, task.id);
    }
  };

  const getInitials = (user: User) => {
    const first = user.firstName?.[0] || '';
    const last = user.lastName?.[0] || '';
    return (first + last).toUpperCase() || user.username?.[0]?.toUpperCase() || '?';
  };

  const [thumbnailUrl, setThumbnailUrl] = React.useState<string | null>(null);
  const [imageError, setImageError] = React.useState(false);

  React.useEffect(() => {
    const fetchThumbnail = async () => {
      if (task.attachments && task.attachments.length > 0) {
        try {
          const fileId = task.attachments[0];
          // Check if it looks like a URL already
          if (fileId.startsWith('http')) {
            setThumbnailUrl(fileId);
            setImageError(false);
            return;
          }
          
          // Fetch the file URL from the API
          const res = await sdk.file.downloadFile(fileId);
          if (res && res.url) {
            // The proxy URL requires authentication, so we need to fetch it with auth headers
            // and convert to a blob URL that can be used in img src
            try {
              const imageResponse = await authenticatedFetch(res.url);
              
              if (imageResponse.ok) {
                const blob = await imageResponse.blob();
                const blobUrl = URL.createObjectURL(blob);
                setThumbnailUrl(blobUrl);
                setImageError(false);
              } else {
                console.error('Failed to fetch image:', imageResponse.status, imageResponse.statusText);
                setThumbnailUrl(null);
                setImageError(true);
              }
            } catch (fetchError) {
              console.error('Failed to fetch image blob:', fetchError);
              setThumbnailUrl(null);
              setImageError(true);
            }
          } else {
            console.warn('No URL returned for file:', fileId);
            setThumbnailUrl(null);
            setImageError(true);
          }
        } catch (error) {
          console.error('Failed to fetch thumbnail URL:', error);
          setThumbnailUrl(null);
          setImageError(true);
        }
      } else {
        setThumbnailUrl(null);
        setImageError(false);
      }
    };

    fetchThumbnail();
    
    // Cleanup blob URL on unmount
    return () => {
      if (thumbnailUrl && thumbnailUrl.startsWith('blob:')) {
        URL.revokeObjectURL(thumbnailUrl);
      }
    };
  }, [task.attachments]);

  return (
    <Card
      className="mb-3 cursor-grab active:cursor-grabbing hover:shadow-md transition-shadow overflow-hidden"
      draggable
      onDragStart={handleDragStart}
      onClick={() => onEdit?.(task)}
    >
      {thumbnailUrl && !imageError && (
        <div className="w-full h-32 overflow-hidden bg-muted/50">
          <img
            src={thumbnailUrl}
            alt={task.title}
            className="w-full h-full object-cover transition-transform hover:scale-105"
            onError={() => {
              console.error('Image failed to load:', thumbnailUrl);
              setImageError(true);
              setThumbnailUrl(null);
            }}
            loading="lazy"
            crossOrigin="anonymous"
          />
        </div>
      )}
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <h4 className="font-semibold text-sm line-clamp-2">{task.title}</h4>
          <Badge
            variant="outline"
            className={`text-[10px] uppercase font-bold px-1.5 py-0 ${getPriorityColor(
              task.priority
            )}`}
          >
            {translate(`pos.project_management.priority.${task.priority}`, task.priority)}
          </Badge>
        </div>

        {task.description && (
          <p className="text-xs text-muted-foreground line-clamp-2">
            {task.description}
          </p>
        )}

        <div className="flex flex-wrap items-center gap-3 text-[10px] text-muted-foreground pt-1">
          {assignee ? (
            <div className="flex items-center gap-1.5">
              {assignee.avatar ? (
                <img
                  src={assignee.avatar}
                  alt={assignee.firstName}
                  className="h-4 w-4 rounded-full"
                />
              ) : (
                <div className="h-4 w-4 rounded-full bg-primary/10 flex items-center justify-center text-[8px] font-bold text-primary">
                  {getInitials(assignee)}
                </div>
              )}
              <span className="font-medium">
                {assignee.firstName} {assignee.lastName}
              </span>
            </div>
          ) : task.assigneeId ? (
            <div className="flex items-center gap-1">
              <UserIcon className="h-3 w-3" />
              <span>{task.assigneeId}</span>
            </div>
          ) : null}
          {task.dueDate && (
            <div className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              <span>{format(new Date(task.dueDate), 'MMM d')}</span>
            </div>
          )}
          {task.estimatedHours && (
            <div className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              <span>{task.estimatedHours}h</span>
            </div>
          )}
        </div>

        {task.tags && task.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {task.tags.map((tag) => (
              <span
                key={tag}
                className="text-[9px] bg-secondary px-1.5 py-0.5 rounded-full"
              >
                #{tag}
              </span>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
